import { Select } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { useListProvidersByUser } from 'api/resource';
import { Organization, Site } from 'interfaces';
import React, { useEffect, useState, useMemo } from 'react';
import { ALL_SITES_VALUE } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

interface ProviderSiteSelectorProps {
  selectedSiteID: string;
  handleSiteChange: (value: string | null) => void;
  setOrganizations: React.Dispatch<React.SetStateAction<Organization[]>>;
  setSites: React.Dispatch<React.SetStateAction<Site[]>>;
  setFetchedOrganizations: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ProviderSiteSelector: React.FC<ProviderSiteSelectorProps> = ({
  selectedSiteID,
  handleSiteChange,
  setOrganizations,
  setSites,
  setFetchedOrganizations,
}) => {
  const { translate } = useTranslation();
  const { id } = useCurrentUserStore();
  const [sitesState, setSitesState] = useState<Site[]>([]);
  const { data: providers = [], isFetched: fetchStatus } =
    useListProvidersByUser(id);

  useEffect(() => {
    if (providers.length) {
      const sites = providers.map((provider) => provider.sites).flat();
      const organizations = sites.map((site) => site.organization);
      setOrganizations(organizations);
      setSites(sites);
      setSitesState(sites);
      setFetchedOrganizations(fetchStatus);
    }
  }, [providers]);

  // Memoized unique list of sites (deduplicate by site.id)
  const uniqueSites = useMemo(() => {
    const map = new Map<string, Site>();
    sitesState.forEach((s) => {
      if (s?.id && !map.has(s.id)) map.set(s.id, s);
    });
    return Array.from(map.values());
  }, [sitesState]);

  // Group sites by organization name, deduping any repeated site ids across providers
  const selectData = useMemo(() => {
    if (!uniqueSites.length) return [];
    const groupMap = new Map<
      string,
      { group: string; items: { value: string; label: string }[] }
    >();
    uniqueSites.forEach((site) => {
      if (!site?.id) return; // skip invalid
      const groupName = site.organization?.name || 'Organization';
      if (!groupMap.has(groupName)) {
        groupMap.set(groupName, { group: groupName, items: [] });
      }
      const group = groupMap.get(groupName)!;
      if (!group.items.some((i) => i.value === site.id)) {
        group.items.push({ value: site.id, label: site.name });
      }
    });
    const groupedSites = Array.from(groupMap.values())
      .sort((a, b) => a.group.localeCompare(b.group))
      .map((g) => ({
        ...g,
        items: g.items.sort((a, b) => a.label.localeCompare(b.label)),
      }));
    return [
      {
        value: ALL_SITES_VALUE,
        label: translate('siteSelector.allSites'),
      },
      ...groupedSites,
    ];
  }, [translate, uniqueSites]);

  return (
    <Select
      data={selectData}
      value={selectedSiteID || uniqueSites[0]?.id || null}
      placeholder={translate('siteSelector.selectSite')}
      data-testid="siteMultiSelect"
      disabled={sitesState?.length === 0 || !fetchStatus}
      onChange={handleSiteChange}
      miw="25%"
      className="site-selector"
    />
  );
};
